import React from 'react';
import { useGetList, useNavigate } from 'react-admin';
import { Box, Typography, Paper, Chip, Table, TableBody, TableCell, TableHead, TableRow, Avatar, TablePagination, useMediaQuery } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { ChipProps } from '@mui/material';
import { LocalOffer } from '@mui/icons-material';
import { ListPageHeader } from '@/components/ListPageHeader';

interface PromotionItem {
  id: string;
  tier: string;
  status: string;
  startDate: string;
  endDate: string;
  trainingProvider?: { type: string; id: string };
  ofoCodes?: string[];
}

const TIER_COLORS: Record<string, ChipProps['color']> = {
  Preferred: 'warning',
  Organic: 'info',
};
const STATUS_COLORS: Record<string, ChipProps['color']> = {
  Active: 'success',
  Expired: 'default',
  Cancelled: 'error',
};

/**
 * Training Provider Promotion List
 *
 * Domain: Skills Development > Training Provider Promotion
 * Description: Lists promotions with tier, status, and date range.
 * @see Documentation/Specification/020_Definitions/application-specific/hr/skills-development/TrainingProviderPromotion.md
 */
export const PromotionList = () => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const { data: promotions } = useGetList('training-provider-promotions', { pagination: { page: page + 1, perPage: rowsPerPage } });
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const stats = {
    total: promotions?.length ?? 0,
    active: promotions?.filter((p: PromotionItem) => p.status === 'Active').length ?? 0,
    expired: promotions?.filter((p: PromotionItem) => p.status === 'Expired').length ?? 0,
    cancelled: promotions?.filter((p: PromotionItem) => p.status === 'Cancelled').length ?? 0,
    preferred: promotions?.filter((p: PromotionItem) => p.tier === 'Preferred').length ?? 0,
  };

  const pieData = [
    { label: 'Active', value: stats.active },
    { label: 'Expired', value: stats.expired },
    { label: 'Cancelled', value: stats.cancelled },
  ].filter(d => d.value > 0);

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <ListPageHeader
          title="Provider Promotions"
          metrics={[
            { label: 'Active', value: String(stats.active), color: 'success.main' },
            { label: 'Preferred', value: String(stats.preferred), color: 'warning.main' },
            { label: 'Total', value: String(stats.total), color: 'primary.main' },
          ]}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {promotions?.map((promo: PromotionItem) => (
            <Paper key={promo.id} sx={{ p: 2 }} onClick={() => navigate(`/training-provider-promotions/${promo.id}/show`)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontSize: 14 }}>
                    <LocalOffer sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{promo.trainingProvider?.id ?? '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{promo.ofoCodes?.length ?? 0} OFO codes</Typography>
                  </Box>
                </Box>
                <Chip label={promo.tier} size="small" color={TIER_COLORS[promo.tier] ?? 'default'} />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  {new Date(promo.startDate).toLocaleDateString()} → {new Date(promo.endDate).toLocaleDateString()}
                </Typography>
                <Chip label={promo.status} size="small" color={STATUS_COLORS[promo.status] ?? 'default'} />
              </Box>
            </Paper>
          ))}
          {(!promotions || promotions.length === 0) && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <LocalOffer sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
              <Typography color="text.secondary">No promotions found</Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader
        title="Provider Promotions"
        metrics={[
          { label: 'Active', value: String(stats.active), color: 'success.main' },
          { label: 'Preferred', value: String(stats.preferred), color: 'warning.main' },
          { label: 'Expired', value: String(stats.expired), color: 'text.secondary' },
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
        ]}
        pieChartData={pieData}
        pieChartTitle="Status"
      />
      <Paper sx={{ mt: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Provider</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Tier</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Start Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>End Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>OFO Codes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {promotions?.map((promo: PromotionItem) => (
              <TableRow
                key={promo.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/training-provider-promotions/${promo.id}/show`)}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 12 }}>
                      <LocalOffer sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Typography variant="body2" fontWeight={500}>{promo.trainingProvider?.id ?? '—'}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={promo.tier} size="small" color={TIER_COLORS[promo.tier] ?? 'default'} />
                </TableCell>
                <TableCell>
                  <Chip label={promo.status} size="small" color={STATUS_COLORS[promo.status] ?? 'default'} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {promo.startDate ? new Date(promo.startDate).toLocaleDateString() : '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {promo.endDate ? new Date(promo.endDate).toLocaleDateString() : '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{promo.ofoCodes?.length ?? 0}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={promotions?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!promotions || promotions.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <LocalOffer sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No promotions found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
